// data-fit LLC · DecisionFlow · ADMIN Stage
// PeriodRollForward.jsx · DDL: R49 · API: v1.27 · Session 95 · Updated S200 · S581 · S584
// Auth: cookie-only — credentials: 'include' on all fetch calls. No token. No Bearer.
// Key constraints: fiscal_period_id INTEGER; roll-forward is irreversible; inline confirm state
// OI-ADMIN-ROLLFORWARD-ENDPOINT: CLOSED v1.12 — §27.4
// S200 fixes: 1) Removed token from useAuth() 2) Response key corrected 3) Tailwind conversion
// S581: Response shape updated to DR-006 full implementation:
//   New fields: wizard_session_id, plans_cloned, plan_assignments_seeded,
//               position_calc_configs_seeded, rollforward_summary
//   Removed stale: from_period / to_period (Option A stub shape)
//   New error code: ROLLFORWARD_ALREADY_EXECUTED
// M040 C2: dark: prefix removed, hardcoded colors → tokens, text-* → text-tok-*
// Loading skeletons → .skeleton CSS class
// S584: OI-ROLLFORWARD-BUTTON-DISABLE — action card permanently replaced by completed state
//       once rollForwardResult is set; Refresh disabled post-success.
//       OI-ROLLFORWARD-PAGE-REFRESH-RESUBMIT — rollForwardResult drives lock for full render
//       lifetime; Refresh button cannot re-enable action card after success.
//       Enhanced recovery guidance added to success card.

import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../../auth/AuthContext.jsx'

const API_BASE = '/api/v1'

const ERROR_MESSAGES = {
  FISCAL_PERIOD_NOT_FOUND: 'The selected fiscal period could not be found.',
  FISCAL_PERIOD_INACTIVE: 'The selected fiscal period is not active and cannot be rolled forward.',
  INVALID_STATUS_TRANSITION: 'This fiscal period cannot be rolled forward from its current status. Period status transitions are open → closed → locked only, with no reversal.',
  NEXT_FISCAL_PERIOD_NOT_FOUND: 'The next fiscal period could not be found. Create or activate the next period before rolling forward.',
  NEXT_FISCAL_PERIOD_NOT_OPEN: 'The next fiscal period is not open. Roll-forward requires the next period to be open.',
  ROLLFORWARD_ALREADY_EXECUTED: 'Roll-forward has already been executed for this period. An active wizard session already exists for the target period.',
  ROLLFORWARD_TRANSACTION_FAILED: 'The roll-forward transaction failed. No changes were committed. Review server logs before retrying.',
  AUTH_INSUFFICIENT_ROLE: 'You do not have permission to roll forward fiscal periods.',
}

function formatDate(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function readErrorCode(errorPayload) {
  return errorPayload?.error?.code || errorPayload?.code || 'UNKNOWN_ERROR'
}

function getErrorMessage(errorCode) {
  return ERROR_MESSAGES[errorCode] || 'An unexpected error occurred while processing the request.'
}

export default function PeriodRollForward({ aiStatus, aiResponse, currentScreen, onNavigate }) {
  const { user } = useAuth()

  const [activePeriod,      setActivePeriod]      = useState(null)
  const [rollForwardResult, setRollForwardResult] = useState(null)
  const [loadingPeriod,     setLoadingPeriod]     = useState(false)
  const [rollingForward,    setRollingForward]    = useState(false)
  const [confirmOpen,       setConfirmOpen]       = useState(false)
  const [confirmChecked,    setConfirmChecked]    = useState(false)
  const [errorCode,         setErrorCode]         = useState('')
  const [errorMessage,      setErrorMessage]      = useState('')

  // S584: rollForwardResult is the authoritative success lock. canRollForward guards
  // the confirm/submit buttons. Neither path is reachable once rollForwardResult is set.
  const canRollForward = Boolean(activePeriod?.fiscal_period_id) && !rollingForward && confirmChecked

  const clearError = useCallback(() => { setErrorCode(''); setErrorMessage('') }, [])

  const fetchActivePeriod = useCallback(async () => {
    setLoadingPeriod(true); clearError()
    try {
      const response = await fetch(`${API_BASE}/fiscal-periods`, { method: 'GET', credentials: 'include' })
      const payload = await response.json().catch(() => null)
      if (!response.ok) {
        const code = readErrorCode(payload)
        setErrorCode(code); setErrorMessage(getErrorMessage(code)); setActivePeriod(null); return
      }
      setActivePeriod(payload?.data?.[0] ?? payload?.fiscal_periods?.[0] ?? null)
    } catch {
      setErrorCode('NETWORK_ERROR')
      setErrorMessage('Unable to fetch the active fiscal period. Check your connection and try again.')
      setActivePeriod(null)
    } finally {
      setLoadingPeriod(false)
    }
  }, [clearError])

  useEffect(() => { fetchActivePeriod() }, [fetchActivePeriod])

  const handleBeginConfirm  = () => { clearError(); setConfirmOpen(true); setConfirmChecked(false) }
  const handleCancelConfirm = () => { setConfirmOpen(false); setConfirmChecked(false) }

  const handleRollForward = async () => {
    if (!activePeriod?.fiscal_period_id || !canRollForward) return
    setRollingForward(true); clearError()
    try {
      const response = await fetch(`${API_BASE}/fiscal-periods/${activePeriod.fiscal_period_id}/roll-forward`, { method: 'POST', credentials: 'include' })
      const payload = await response.json().catch(() => null)
      if (!response.ok) { const code = readErrorCode(payload); setErrorCode(code); setErrorMessage(getErrorMessage(code)); return }
      setRollForwardResult(payload)
      setActivePeriod(null)   // period list will reload via Refresh; don't clobber with stale shape
      setConfirmOpen(false); setConfirmChecked(false)
    } catch {
      setErrorCode('NETWORK_ERROR')
      setErrorMessage('Unable to complete roll-forward. Check your connection and try again.')
    } finally {
      setRollingForward(false)
    }
  }

  // S584: rollForwardResult is set — determines which action card branch to render.
  // Once set it is never cleared, so the completed state is permanent for this render lifetime.
  // Hard page refresh (F5) resets all state, but the server will return ROLLFORWARD_ALREADY_EXECUTED
  // on any retry attempt, and fetchActivePeriod will return the now-active next period which
  // does not satisfy the server preconditions for roll-forward anyway.
  const rollForwardComplete = Boolean(rollForwardResult)

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">ADMIN-07</p>
          <h2 className="mt-0.5 text-tok-lg font-semibold text-[rgb(var(--color-text))]">Period Roll-Forward</h2>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">Activate the next open period. The source period must already be locked.</p>
        </div>
        {/* S584: Refresh disabled after success — there is nothing to refresh once roll-forward
            is complete, and re-fetching would repopulate activePeriod unnecessarily. */}
        <button
          type="button"
          onClick={fetchActivePeriod}
          disabled={loadingPeriod || rollingForward || rollForwardComplete}
          className="shrink-0 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50"
        >
          {loadingPeriod ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Irreversible action warning */}
      <div className="rounded-xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/10 px-4 py-3 text-tok-sm text-[rgb(var(--color-warning))]">
        <strong>Irreversible action.</strong>{' '}Period status transitions are open → closed → locked only. There is no reversal. Closing and locking details are recorded server-side from your session.
      </div>

      {/* Error banner */}
      {errorMessage && (
        <div role="alert" className="rounded-xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/10 px-4 py-3 text-tok-sm">
          <span className="font-semibold text-[rgb(var(--color-danger))]">{errorCode || 'ERROR'}</span>
          <span className="ml-2 text-[rgb(var(--color-danger))]">{errorMessage}</span>
        </div>
      )}

      {/* Active period card */}
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
        <div className="border-b border-[rgb(var(--color-border))] px-4 py-3">
          <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Current active fiscal period</h3>
        </div>
        <div className="px-4 py-4">
          {loadingPeriod ? (
            <div className="space-y-2">
              <div className="skeleton h-4 w-2/3 rounded" />
              <div className="skeleton h-4 w-1/2 rounded" />
            </div>
          ) : activePeriod ? (
            <div className="grid gap-3 sm:grid-cols-3">
              {[
                { label: 'Period code',  value: activePeriod.period_code  || '—' },
                { label: 'Period label', value: activePeriod.period_label || '—' },
                { label: 'Status',       value: activePeriod.period_status || '—' },
                { label: 'Start date',   value: formatDate(activePeriod.period_start_date) },
                { label: 'End date',     value: formatDate(activePeriod.period_end_date) },
                { label: 'Period',       value: activePeriod.period_label || activePeriod.period_code || String(activePeriod.fiscal_period_id) },
              ].map(({ label, value }) => (
                <div key={label}>
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">{label}</p>
                  <p className="mt-0.5 text-tok-sm font-medium text-[rgb(var(--color-text))]">{value}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No active fiscal period was returned.</p>
          )}
        </div>
      </div>

      {/* Roll-forward action card
          S584 — three-way branch:
            1. rollForwardComplete → completed state (permanent, no re-enable path)
            2. !confirmOpen        → begin button
            3. confirmOpen         → confirm flow
          Branch 1 takes priority over 2 and 3 unconditionally. */}
      <div className="rounded-xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-surface))]">
        <div className="border-b border-[rgb(var(--color-danger))]/20 px-4 py-3">
          <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Roll forward period</h3>
          <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">Activates the next open fiscal period. Source period must be locked before proceeding.</p>
        </div>
        <div className="px-4 py-4">
          {rollForwardComplete ? (
            /* S584: OI-ROLLFORWARD-BUTTON-DISABLE — completed state replaces action UI permanently.
               No re-enable path exists in this component. Roll-forward is a one-way period
               transition enforced server-side. Recovery from a bad plan clone must be handled
               via the wizard session for the new period, not by re-running roll-forward. */
            <div className="flex items-center gap-3 rounded-lg border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 px-4 py-3">
              <span className="text-[rgb(var(--color-success))] text-tok-lg" aria-hidden="true">✓</span>
              <div>
                <p className="text-tok-sm font-semibold text-[rgb(var(--color-success))]">Roll-forward completed</p>
                <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                  This action cannot be re-run. The period transition is recorded server-side.
                </p>
              </div>
            </div>
          ) : !confirmOpen ? (
            <button
              type="button"
              onClick={handleBeginConfirm}
              disabled={!activePeriod?.fiscal_period_id || loadingPeriod || rollingForward}
              className="rounded-lg bg-[rgb(var(--color-danger))] px-4 py-2 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-danger))]/80 disabled:opacity-50"
            >
              Begin roll-forward confirmation
            </button>
          ) : (
            <div className="space-y-4">
              <h4 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Confirm irreversible roll-forward</h4>
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                You are about to roll forward period{' '}
                <strong className="text-[rgb(var(--color-text))]">{activePeriod?.period_code || activePeriod?.fiscal_period_id}</strong>. This action cannot be reversed.
              </p>
              <label className="flex items-start gap-3 text-tok-sm text-[rgb(var(--color-text))]">
                <input type="checkbox" checked={confirmChecked} onChange={(e) => setConfirmChecked(e.target.checked)} disabled={rollingForward} className="mt-0.5 h-4 w-4 rounded border-[rgb(var(--color-border))]" />
                <span>I understand this will activate the next open period. The source period must already be locked. This action is irreversible.</span>
              </label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleRollForward}
                  disabled={!canRollForward}
                  className="rounded-lg bg-[rgb(var(--color-danger))] px-4 py-2 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-danger))]/80 disabled:opacity-50"
                >
                  {rollingForward ? 'Rolling forward…' : 'Confirm roll-forward'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelConfirm}
                  disabled={rollingForward}
                  className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Success result card — S581 response shape */}
      {rollForwardResult && (
        <div className="rounded-xl border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-surface))]">
          <div className="border-b border-[rgb(var(--color-success))]/20 px-4 py-3">
            <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-success))]">Roll-forward completed</h3>
            <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
              {rollForwardResult.period_label || rollForwardResult.period_code} is now active.
              A new wizard session has been created — re-approval of cloned plans is required before calculation.
            </p>
          </div>
          <div className="px-4 py-4 space-y-4">
            {/* Period details */}
            <div className="grid gap-3 sm:grid-cols-3">
              {[
                { label: 'Period code',   value: rollForwardResult.period_code   || '—' },
                { label: 'Period label',  value: rollForwardResult.period_label  || '—' },
                { label: 'Status',        value: rollForwardResult.period_status || '—' },
                { label: 'Start date',    value: formatDate(rollForwardResult.period_start_date) },
                { label: 'End date',      value: formatDate(rollForwardResult.period_end_date) },
                { label: 'Active',        value: rollForwardResult.is_active ? 'Yes' : 'No' },
              ].map(({ label, value }) => (
                <div key={label}>
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">{label}</p>
                  <p className="mt-0.5 text-tok-sm font-medium text-[rgb(var(--color-text))]">{value}</p>
                </div>
              ))}
            </div>

            {/* Carry-forward summary */}
            <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3">
              <p className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] mb-2">Carry-forward summary</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {[
                  { label: 'Plans cloned (as draft)',      value: rollForwardResult.plans_cloned ?? '—' },
                  { label: 'Plan assignments seeded',      value: rollForwardResult.plan_assignments_seeded ?? '—' },
                  { label: 'Calc configs seeded',          value: rollForwardResult.position_calc_configs_seeded ?? '—' },
                  { label: 'Wizard session ID',            value: rollForwardResult.wizard_session_id
                      ? `${String(rollForwardResult.wizard_session_id).slice(0, 8)}…`
                      : '—' },
                ].map(({ label, value }) => (
                  <div key={label} className="flex justify-between text-tok-sm">
                    <span className="text-[rgb(var(--color-text-muted))]">{label}</span>
                    <span className="font-medium text-[rgb(var(--color-text))]">{String(value)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Next steps callout — S584: enhanced with actionable recovery guidance */}
            <div className="rounded-lg border border-[rgb(var(--color-primary))]/20 bg-[rgb(var(--color-primary))]/5 px-4 py-3 text-tok-xs text-[rgb(var(--color-text-muted))]">
              <strong className="text-[rgb(var(--color-primary))]">Next steps:</strong>{' '}
              Cloned plans are in <strong>draft</strong> status and require re-approval before calculation is eligible.
              Load fresh quota and credits for the new period, then run a preview calculation.
            </div>

            {/* S584: Recovery guidance — wizard session repair path, not this screen */}
            <div className="rounded-lg border border-[rgb(var(--color-warning))]/20 bg-[rgb(var(--color-warning))]/5 px-4 py-3 text-tok-xs text-[rgb(var(--color-text-muted))]">
              <strong className="text-[rgb(var(--color-warning))]">If plan clones look incorrect:</strong>{' '}
              Do not attempt to re-run roll-forward — the period transition is permanent. Navigate to the wizard session for the new period to inspect cloned plans and assignments. If the session or clone data needs correction, contact your platform administrator to repair the wizard session directly.
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
